import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ticketAPI, adminAPI, metadataAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Ticket, Clock, AlertTriangle, CheckCircle, XCircle, ArrowRight, BarChart3 } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell,
} from 'recharts';

export default function DashboardPage() {
  const [stats, setStats] = useState({ open: 0, inProgress: 0, resolved: 0, closed: 0, rejected: 0, total: 0 });
  const [recentTickets, setRecentTickets] = useState([]);
  const [telemetry, setTelemetry] = useState(null);
  const [clientBreakdown, setClientBreakdown] = useState([]);
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [loading, setLoading] = useState(true);
  const { user, isClient, isStaff, isAdmin, isSupportLead } = useAuth();
  const navigate = useNavigate();

  useEffect(() => { loadDashboard(); }, [selectedClientId]);

  const loadDashboard = async () => {
    try {
      const params = { page_size: 100, page: 1 };
      if (selectedClientId) params.client_id = selectedClientId;
      const resp = await ticketAPI.list(params);
      const items = resp.data.items;

      const open = items.filter(t => t.status === 'OPEN').length;
      const inProgress = items.filter(t =>
        ['TRIAGED', 'ASSIGNED_GROUP', 'ASSIGNED_INDIVIDUAL', 'RESOLUTION_COMMENCED'].includes(t.status)
      ).length;
      const resolved = items.filter(t =>
        ['RESOLUTION_COMPLETED', 'VERIFIED', 'COMMUNICATED'].includes(t.status)
      ).length;
      const closed = items.filter(t => t.status === 'CLOSED').length;
      const rejected = items.filter(t => t.status === 'REJECTED').length;

      setStats({ open, inProgress, resolved, closed, rejected, total: resp.data.total });
      setRecentTickets(items.slice(0, 5));

      // Load telemetry + client breakdown if staff with admin/lead access
      if (isStaff && (isAdmin || isSupportLead)) {
        try {
          const telResp = await adminAPI.getDashboardTelemetry(selectedClientId || undefined);
          setTelemetry(telResp.data);
        } catch (err) {
          console.log('Telemetry not available:', err.message);
        }
        try {
          const bkResp = await adminAPI.getClientBreakdown();
          setClientBreakdown(bkResp.data.breakdown || []);
        } catch {}
        // Load client list for filter dropdown
        if (clients.length === 0) {
          try {
            const clResp = await metadataAPI.getClients();
            setClients(clResp.data);
          } catch {}
        }
      }
    } catch (err) {
      console.error('Dashboard load failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { label: 'Open', value: stats.open, icon: Ticket, color: '#3b82f6', bg: '#eff6ff' },
    { label: 'In progress', value: stats.inProgress, icon: Clock, color: '#d97706', bg: '#fffbeb' },
    { label: 'Resolved', value: stats.resolved, icon: CheckCircle, color: '#059669', bg: '#ecfdf5' },
    { label: 'Closed', value: stats.closed, icon: CheckCircle, color: '#6b7280', bg: '#f3f4f6' },
    { label: 'Rejected', value: stats.rejected, icon: XCircle, color: '#dc2626', bg: '#fef2f2' },
  ];

  const CHART_COLORS = ['#3b82f6', '#059669', '#dc2626', '#d97706', '#8b5cf6'];

  return (
    <div>
      <div className="page-header">
        <h1>Dashboard</h1>
        <p>
          {isClient
            ? `Welcome back, ${user?.name}. Here's your support overview.`
            : 'Support overview for all clients'
          }
        </p>
      </div>

      {isStaff && (isAdmin || isSupportLead) && clients.length > 0 && (
          <div style={{ marginBottom: 16, maxWidth: 300 }}>
            <select className="form-select" value={selectedClientId}
                    onChange={e => setSelectedClientId(e.target.value)}>
              <option value="">All clients</option>
              {clients.map(c => (
                  <option key={c.id} value={c.id}>{c.client_name} ({c.client_code})</option>
              ))}
            </select>
          </div>
      )}

      {loading ? (
        <p style={{ color: 'var(--color-text-muted)' }}>Loading dashboard...</p>
      ) : (
        <>
          {/* Stat cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 16, marginBottom: 24 }}>
            {statCards.map(({ label, value, icon: Icon, color, bg }) => (
              <div key={label} className="card" style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{
                  width: 42, height: 42, borderRadius: 10,
                  background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Icon size={20} color={color} />
                </div>
                <div>
                  <div style={{ fontSize: 22, fontWeight: 700, color }}>{value}</div>
                  <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{label}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Telemetry charts (staff only) */}
          {telemetry && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
              {/* Monthly open/closed chart */}
              {telemetry.monthly_counts?.length > 0 && (
                <div className="card">
                  <div className="card-title" style={{ marginBottom: 16 }}>
                    <BarChart3 size={14} style={{ marginRight: 6 }} />
                    Monthly ticket volume
                  </div>
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={telemetry.monthly_counts}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                      <Tooltip contentStyle={{ fontSize: 12 }} />
                      <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
                      <Bar dataKey="opened" name="Opened" fill="#3b82f6" radius={[3,3,0,0]} />
                      <Bar dataKey="closed" name="Closed" fill="#059669" radius={[3,3,0,0]} />
                      <Bar dataKey="rejected" name="Rejected" fill="#dc2626" radius={[3,3,0,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* SLA performance pie/indicator */}
              {telemetry.sla_performance?.length > 0 && (
                <div className="card">
                  <div className="card-title" style={{ marginBottom: 16 }}>SLA compliance</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {telemetry.sla_performance.map((s, idx) => (
                      <div key={s.severity_id}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                          <span style={{ fontSize: 13, fontWeight: 500 }}>{s.severity}</span>
                          <span style={{
                            fontSize: 12, fontWeight: 600,
                            color: s.compliance_pct >= 95 ? '#059669' : s.compliance_pct >= 80 ? '#d97706' : '#dc2626',
                          }}>
                            {s.compliance_pct}% ({s.met}/{s.total})
                          </span>
                        </div>
                        <div style={{
                          height: 8, borderRadius: 4, background: '#e5e7eb', overflow: 'hidden',
                        }}>
                          <div style={{
                            height: '100%', borderRadius: 4,
                            width: `${s.compliance_pct}%`,
                            background: s.compliance_pct >= 95 ? '#059669' : s.compliance_pct >= 80 ? '#d97706' : '#dc2626',
                            transition: 'width 0.5s ease',
                          }} />
                        </div>
                      </div>
                    ))}
                  </div>
                  {telemetry.sla_performance.every(s => s.total === 0) && (
                    <p style={{ color: 'var(--color-text-muted)', fontSize: 13, marginTop: 8 }}>No ticket data yet for SLA analysis.</p>
                  )}
                </div>
              )}

              {/* Priority-wise TAT vs SLA */}
              {telemetry.priority_tat?.length > 0 && (
                <div className="card" style={{ gridColumn: '1 / -1' }}>
                  <div className="card-title" style={{ marginBottom: 16 }}>
                    Average resolution time vs SLA target (hours)
                  </div>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={telemetry.priority_tat} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis type="number" tick={{ fontSize: 11 }} />
                      <YAxis type="category" dataKey="severity" tick={{ fontSize: 12 }} width={80} />
                      <Tooltip contentStyle={{ fontSize: 12 }}
                        formatter={(val, name) => [`${val}h`, name]} />
                      <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
                      <Bar dataKey="avg_tat_hours" name="Avg TAT" fill="#3b82f6" radius={[0,3,3,0]} />
                      <Bar dataKey="sla_target_hours" name="SLA Target" fill="#e5e7eb" radius={[0,3,3,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          )}

          {clientBreakdown.length > 1 && (
              <div className="card" style={{ marginBottom: 24 }}>
                <div className="card-title" style={{ marginBottom: 12 }}>Client-wise breakdown</div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead><tr>
                    <th style={{ textAlign: 'left', padding: '8px', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', color: 'var(--color-text-muted)' }}>Client</th>
                    <th style={{ textAlign: 'center', padding: '8px', fontSize: 11 }}>Open</th>
                    <th style={{ textAlign: 'center', padding: '8px', fontSize: 11 }}>In Progress</th>
                    <th style={{ textAlign: 'center', padding: '8px', fontSize: 11 }}>Resolved</th>
                    <th style={{ textAlign: 'center', padding: '8px', fontSize: 11 }}>Closed</th>
                    <th style={{ textAlign: 'center', padding: '8px', fontSize: 11, fontWeight: 700 }}>Total</th>
                  </tr></thead>
                  <tbody>
                  {clientBreakdown.map(b => (
                      <tr key={b.client_id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                        <td style={{ padding: '8px', fontWeight: 500 }}>{b.client_name} ({b.client_code})</td>
                        <td style={{ textAlign: 'center', padding: '8px', color: '#3b82f6' }}>{b.open}</td>
                        <td style={{ textAlign: 'center', padding: '8px', color: '#d97706' }}>{b.in_progress}</td>
                        <td style={{ textAlign: 'center', padding: '8px', color: '#059669' }}>{b.resolved}</td>
                        <td style={{ textAlign: 'center', padding: '8px', color: '#6b7280' }}>{b.closed}</td>
                        <td style={{ textAlign: 'center', padding: '8px', fontWeight: 700 }}>{b.total}</td>
                      </tr>
                  ))}
                  </tbody>
                </table>
              </div>
          )}

          {/* Recent tickets */}
          <div className="card">
            <div className="card-header">
              <span className="card-title">Recent tickets</span>
              <button className="btn btn-sm" onClick={() => navigate('/tickets')}>
                View all <ArrowRight size={14} />
              </button>
            </div>
            {recentTickets.length === 0 ? (
              <p style={{ color: 'var(--color-text-muted)', textAlign: 'center', padding: 40 }}>
                No tickets yet. Click "Raise ticket" to create your first one.
              </p>
            ) : (
              <div className="table-wrapper">
                <table>
                  <thead>
                    <tr>
                      <th>Issue ID</th>
                      <th>Feature</th>
                      <th>Severity</th>
                      <th>Status</th>
                      <th>Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentTickets.map(t => (
                      <tr key={t.id} onClick={() => navigate(`/tickets/${t.id}`)}>
                        <td style={{ fontWeight: 600, color: 'var(--color-primary)' }}>{t.issue_id}</td>
                        <td>{t.feature}</td>
                        <td>{t.severity}</td>
                        <td>{t.status_display}</td>
                        <td style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
                          {new Date(t.created_at).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
